<template>
  <div class="page-main">
    <div class="page-top">
      <div class="flex flex-y-center">
        <span class="title">工艺流程图</span>
        <span class="c-gray m-l-10">每条泳道是一个工艺，泳道内按顺序排工序，箭头表示先后。拖动工序块调整位置，拖右下角调整大小；用 +/- 整图缩放。</span>
      </div>
      <div class="flex flex-y-center">
        <el-button-group class="m-l-10 m-r-10">
          <el-button size="mini" @click="zoomOut">－</el-button>
          <el-button size="mini" @click="zoomReset">{{ Math.round(zoom * 100) }}%</el-button>
          <el-button size="mini" @click="zoomIn">＋</el-button>
        </el-button-group>
        <el-checkbox v-model="showArrows" class="m-r-10">工序连线</el-checkbox>
        <el-button size="mini" @click="autoLayout">全部重排</el-button>
        <el-button size="mini" @click="reload">刷新</el-button>
        <el-button type="success" size="mini" :loading="saving" @click="saveLayout">保存布局</el-button>
      </div>
    </div>

    <div class="content">
      <div class="canvas-wrap" :style="canvasWrapStyle">
        <div class="canvas-sizer" :style="sizerStyle">
          <div class="canvas" :style="canvasStyle">
            <!-- 工序连线 -->
            <svg v-if="showArrows" class="links" :width="canvas.w" :height="canvas.h">
              <defs>
                <marker id="arrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                  <path d="M0,0 L6,3 L0,6 Z" fill="#98a2b3" />
                </marker>
              </defs>
              <line v-for="(ln, i) in linkList" :key="'lk' + i" :x1="ln.x1" :y1="ln.y1" :x2="ln.x2" :y2="ln.y2" stroke="#98a2b3" stroke-width="1.5" marker-end="url(#arrow)" />
            </svg>

            <div
              v-for="gy in laneList"
              :key="'gy-' + gy.id"
              class="lane-block"
              :class="{ active: selected && selected.type === 'lane' && selected.id === gy.id }"
              :style="laneStyle(gy.id)"
            >
              <div class="lane-head" :style="{ background: laneColor(gy) }" @mousedown="startMove('lane', gy.id, $event)">
                <span class="lane-name">{{ gy.name }}</span>
                <span class="lane-meta">{{ gy.code }} · {{ gy.lei_mu_name || '未分类' }} · {{ stepsOf(gy.id).length }} 道工序</span>
              </div>
              <div class="lane-body">
                <div
                  v-for="st in stepsOf(gy.id)"
                  :key="'st-' + gy.id + '-' + st.gongxuId"
                  class="step-box"
                  :class="{ active: selected && selected.type === 'step' && selected.id === st.gongxuId && selected.gongyiId === gy.id }"
                  :style="stepStyle(gy.id, st.gongxuId)"
                  @mousedown="startMove('step', st.gongxuId, $event, gy.id)"
                >
                  <div class="step-idx">{{ st.seq }}</div>
                  <div class="step-name" :title="st.name">{{ st.name }}</div>
                  <div class="step-info">{{ st.time ? st.time + ' 工时' : (st.lei_xing || '工序') }}</div>
                  <div class="resize-handle step-resize" @mousedown.stop="startResize('step', st.gongxuId, $event, gy.id)"></div>
                </div>
              </div>
              <div class="resize-handle lane-resize" @mousedown.stop="startResize('lane', gy.id, $event)"></div>
            </div>

            <div v-if="!laneList.length" class="canvas-empty">暂无工艺数据，请先到「工艺列表」创建加工工艺。</div>
          </div>
        </div>
      </div>

      <div class="detail-panel">
        <template v-if="selected && selected.type === 'step'">
          <div class="detail-title">{{ selectedStep.name }}</div>
          <div class="detail-sub">编码：{{ selectedStep.code || '-' }}</div>
          <div class="detail-sub">类型：{{ selectedStep.lei_xing || '-' }}</div>
          <div class="detail-sub">分组：{{ selectedStep.fen_zu || '-' }}</div>
          <div class="detail-divider"></div>
          <div class="detail-count">标准工时：{{ selectedStep.time || '-' }}</div>
          <div class="detail-count">工序成本：{{ selectedStep.cost || '-' }}</div>
          <div class="detail-divider"></div>
          <div class="detail-sub">所属工艺：{{ gyName(selected.gongyiId) }}</div>
        </template>
        <template v-else-if="selected && selected.type === 'lane'">
          <div class="detail-title">{{ selectedLane.name }}</div>
          <div class="detail-sub">编码：{{ selectedLane.code || '-' }}</div>
          <div class="detail-sub">类目：{{ selectedLane.lei_mu_name || '-' }}</div>
          <div class="detail-divider"></div>
          <div class="detail-count">工序：{{ stepsOf(selectedLane.id).length }} 道</div>
          <div class="detail-list">
            <div v-for="st in stepsOf(selectedLane.id)" :key="st.gongxuId" class="detail-row">
              <div class="dr-name">{{ st.seq }}. {{ st.name }}</div>
              <div class="dr-info"><span class="c-gray">工时</span><span class="dr-qty">{{ st.time || '-' }}</span></div>
            </div>
            <div v-if="!stepsOf(selectedLane.id).length" class="c-gray p-10">该工艺尚未关联工序。</div>
          </div>
          <div class="detail-divider"></div>
          <div class="detail-sub">关联设备：{{ (selectedLane.she_bei_list || []).join('、') || '-' }}</div>
        </template>
        <template v-else>
          <div class="c-gray p-10">点击工艺或工序查看详情。</div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
const GRID = 10
const LANE_HEAD_H = 34
const PALETTE = ['#409eff', '#67c23a', '#e6a23c', '#f56c6c', '#909399', '#705cfa', '#13c2c2', '#fa8c16']

export default {
  data() {
    return {
      saving: false,
      zoom: 1,
      showArrows: true,
      laneList: [], // {id,code,name,lei_mu_name,she_bei_list}
      stepList: [], // {gongyiId,gongxuId,name,code,lei_xing,fen_zu,time,cost,order,seq}
      laneGeo: {}, // gongyiId -> {x,y,w,h} 绝对
      stepGeo: {}, // 'gongyiId-gongxuId' -> {x,y,w,h} 相对泳道 body
      canvas: { w: 2000, h: 1200 },
      wrapH: 400,
      drag: null,
      selected: null
    }
  },
  computed: {
    selectedLane() {
      return this.selected && this.selected.type === 'lane' ? this.laneList.find((g) => String(g.id) === String(this.selected.id)) : null
    },
    selectedStep() {
      if (!this.selected || this.selected.type !== 'step') return null
      return this.stepList.find((s) => String(s.gongxuId) === String(this.selected.id) && String(s.gongyiId) === String(this.selected.gongyiId))
    },
    canvasWrapStyle() {
      return { height: this.wrapH + 'px' }
    },
    sizerStyle() {
      return { width: this.canvas.w * this.zoom + 'px', height: this.canvas.h * this.zoom + 'px' }
    },
    canvasStyle() {
      return { width: this.canvas.w + 'px', height: this.canvas.h + 'px', transform: 'scale(' + this.zoom + ')', transformOrigin: '0 0' }
    },
    // 工序连线：同一泳道内按 seq 相邻两点连线（绝对坐标）
    linkList() {
      const links = []
      this.laneList.forEach((gy) => {
        const lg = this.laneGeo[gy.id]
        if (!lg) return
        const steps = this.stepsOf(gy.id)
        for (let i = 0; i < steps.length - 1; i++) {
          const a = this.stepGeo[gy.id + '-' + steps[i].gongxuId]
          const b = this.stepGeo[gy.id + '-' + steps[i + 1].gongxuId]
          if (!a || !b) continue
          links.push({
            x1: lg.x + a.x + a.w,
            y1: lg.y + LANE_HEAD_H + a.y + a.h / 2,
            x2: lg.x + b.x,
            y2: lg.y + LANE_HEAD_H + b.y + b.h / 2
          })
        }
      })
      return links
    }
  },
  watch: {
    zoom() {
      this.saveView()
    },
    showArrows() {
      this.saveView()
    }
  },
  mounted() {
    this.loadView()
    this.init()
    this.calcWrapH()
    window.addEventListener('mousemove', this.onMove)
    window.addEventListener('mouseup', this.onUp)
    window.addEventListener('resize', this.calcWrapH)
  },
  beforeDestroy() {
    window.removeEventListener('mousemove', this.onMove)
    window.removeEventListener('mouseup', this.onUp)
    window.removeEventListener('resize', this.calcWrapH)
  },
  methods: {
    async init() {
      await this.loadData()
      await this.loadLayout()
    },
    reload() {
      this.selected = null
      this.init()
    },
    // 画布高度按自身到视口顶部的实际距离动态计算，正好填满到底部，避免外层再出现一条全局竖向滚动条
    calcWrapH() {
      this.$nextTick(() => {
        const el = this.$el && this.$el.querySelector('.canvas-wrap')
        if (!el) return
        const top = el.getBoundingClientRect().top
        this.wrapH = Math.max(240, window.innerHeight - top - 16)
      })
    },
    loadData() {
      return this.$api({ method: 'post', url: '/admin/gcvip/productionLayout/processData', data: {} }).then((res) => {
        const d = res.data || {}
        this.laneList = (d.gongyiList || []).map((g) => ({ id: g.id, code: g.code, name: g.name, lei_mu_name: g.lei_mu_name, she_bei_list: g.she_bei_list || [] }))
        // steps 已在后端 join 好工序详情，前端直接渲染
        this.stepList = d.steps || []
      })
    },
    loadLayout() {
      return this.$api({ method: 'get', url: '/admin/gcvip/productionLayout/find?scope=process' })
        .then((res) => {
          const payload = res.data && res.data.data
          if (payload && payload.layout && payload.layout.processes && payload.layout.processes.length) {
            this.canvas.w = payload.canvas_w || 2000
            this.canvas.h = payload.canvas_h || 1200
            const laneGeo = {}
            const stepGeo = {}
            ;(payload.layout.processes || []).forEach((p) => (laneGeo[p.gongyiId] = { x: p.x, y: p.y, w: p.w, h: p.h }))
            ;(payload.layout.steps || []).forEach((s) => (stepGeo[s.gongyiId + '-' + s.gongxuId] = { x: s.x, y: s.y, w: s.w, h: s.h }))
            this.laneGeo = laneGeo
            this.stepGeo = stepGeo
            this.ensureAllPlaced()
          } else {
            this.autoLayout()
          }
        })
        .catch(() => this.autoLayout())
    },

    // ---- 排布 ----
    autoLayout() {
      const gap = 24
      const off = 24
      const LW = 1400
      const LH = 160
      const laneGeo = {}
      this.laneList.forEach((gy, i) => {
        laneGeo[gy.id] = { x: off, y: off + i * (LH + gap), w: LW, h: LH }
      })
      this.laneGeo = laneGeo
      const stepGeo = {}
      this.laneList.forEach((gy) => {
        const steps = this.stepsOf(gy.id)
        const pad = 12
        const sgap = 40
        const sw = 150
        const sh = 76
        steps.forEach((st, idx) => {
          stepGeo[gy.id + '-' + st.gongxuId] = { x: pad + idx * (sw + sgap), y: pad, w: sw, h: sh }
        })
      })
      this.stepGeo = stepGeo
      this.fitCanvas()
    },
    ensureAllPlaced() {
      this.purgeStale()
      let maxY = 24
      Object.keys(this.laneGeo).forEach((k) => {
        const g = this.laneGeo[k]
        maxY = Math.max(maxY, g.y + g.h + 24)
      })
      this.laneList.forEach((gy) => {
        if (!this.laneGeo[gy.id]) {
          this.$set(this.laneGeo, gy.id, { x: 24, y: maxY, w: 1400, h: 160 })
          maxY += 160 + 24
        }
        const steps = this.stepsOf(gy.id)
        const lg = this.laneGeo[gy.id]
        let rightMost = 12
        steps.forEach((st) => {
          const key = gy.id + '-' + st.gongxuId
          if (!this.stepGeo[key]) {
            const sw = 150
            const sh = 76
            this.$set(this.stepGeo, key, { x: rightMost + 40, y: 12, w: sw, h: sh })
          }
          rightMost = this.stepGeo[key].x + this.stepGeo[key].w
          // 泳道宽度不足时自动加宽
          if (rightMost + 12 > lg.w) lg.w = rightMost + 12
        })
      })
      this.fitCanvas()
    },
    purgeStale() {
      const gyIds = this.laneList.map((g) => String(g.id))
      Object.keys(this.laneGeo).forEach((id) => gyIds.indexOf(String(id)) === -1 && this.$delete(this.laneGeo, id))
      const keys = this.stepList.map((s) => s.gongyiId + '-' + s.gongxuId)
      Object.keys(this.stepGeo).forEach((k) => keys.indexOf(k) === -1 && this.$delete(this.stepGeo, k))
    },
    fitCanvas() {
      let maxX = 1200
      let maxY = 800
      Object.keys(this.laneGeo).forEach((id) => {
        const g = this.laneGeo[id]
        maxX = Math.max(maxX, g.x + g.w + 40)
        maxY = Math.max(maxY, g.y + g.h + 40)
      })
      this.canvas.w = maxX
      this.canvas.h = maxY
    },

    // ---- 拖拽/缩放 ----
    startMove(type, id, e, gongyiId) {
      e.preventDefault()
      this.selected = type === 'step' ? { type, id, gongyiId } : { type, id }
      const geo = type === 'lane' ? this.laneGeo[id] : this.stepGeo[gongyiId + '-' + id]
      if (!geo) return
      this.drag = { mode: 'move', type, id, gongyiId, startX: e.clientX, startY: e.clientY, orig: { x: geo.x, y: geo.y } }
    },
    startResize(type, id, e, gongyiId) {
      e.preventDefault()
      this.selected = type === 'step' ? { type, id, gongyiId } : { type, id }
      const geo = type === 'lane' ? this.laneGeo[id] : this.stepGeo[gongyiId + '-' + id]
      if (!geo) return
      this.drag = { mode: 'resize', type, id, gongyiId, startX: e.clientX, startY: e.clientY, orig: { w: geo.w, h: geo.h } }
    },
    onMove(e) {
      if (!this.drag) return
      const dx = (e.clientX - this.drag.startX) / this.zoom
      const dy = (e.clientY - this.drag.startY) / this.zoom
      const { mode, type, id, gongyiId, orig } = this.drag
      if (type === 'lane') {
        const g = this.laneGeo[id]
        if (mode === 'move') {
          g.x = this.snap(Math.max(0, orig.x + dx))
          g.y = this.snap(Math.max(0, orig.y + dy))
        } else {
          g.w = this.snap(Math.max(300, orig.w + dx))
          g.h = this.snap(Math.max(120, orig.h + dy))
        }
        this.fitCanvas()
      } else {
        const key = gongyiId + '-' + id
        const g = this.stepGeo[key]
        const lg = this.laneGeo[gongyiId] || { w: 1400, h: 160 }
        const bodyW = lg.w
        const bodyH = lg.h - LANE_HEAD_H
        if (mode === 'move') {
          g.x = this.snap(Math.max(0, orig.x + dx))
          g.y = this.snap(Math.max(0, Math.min(orig.y + dy, bodyH - g.h)))
          if (g.x + g.w + 12 > bodyW) lg.w = this.snap(g.x + g.w + 12)
        } else {
          g.w = this.snap(Math.max(90, orig.w + dx))
          g.h = this.snap(Math.max(50, Math.min(orig.h + dy, bodyH - g.y)))
          if (g.x + g.w + 12 > bodyW) lg.w = this.snap(g.x + g.w + 12)
        }
      }
    },
    onUp() {
      if (this.drag && this.drag.type === 'lane') this.fitCanvas()
      this.drag = null
    },
    snap(v) {
      return Math.round(v / GRID) * GRID
    },

    // ---- 保存 ----
    saveLayout() {
      this.saving = true
      const processes = this.laneList.filter((g) => this.laneGeo[g.id]).map((g) => Object.assign({ gongyiId: g.id }, this.laneGeo[g.id]))
      const steps = []
      this.stepList.forEach((s) => {
        const geo = this.stepGeo[s.gongyiId + '-' + s.gongxuId]
        if (!geo) return
        steps.push({ gongyiId: s.gongyiId, gongxuId: s.gongxuId, x: geo.x, y: geo.y, w: geo.w, h: geo.h })
      })
      this.$api({
        method: 'post',
        url: '/admin/gcvip/productionLayout/save',
        data: { scope: 'process', layout: { processes, steps }, canvas_w: this.canvas.w, canvas_h: this.canvas.h }
      })
        .then((res) => {
          if (res.data.state !== 'fail') this.$message.success('布局已保存')
        })
        .catch(() => {})
        .then(() => (this.saving = false))
    },

    // ---- 辅助 ----
    stepsOf(gongyiId) {
      return this.stepList
        .filter((s) => String(s.gongyiId) === String(gongyiId))
        .slice()
        .sort((a, b) => (a.order || 0) - (b.order || 0))
    },
    gyName(id) {
      const g = this.laneList.find((x) => String(x.id) === String(id))
      return g ? g.name : ''
    },
    laneColor(gy) {
      const key = gy.lei_mu_name || gy.name || ''
      let h = 0
      for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0
      return PALETTE[h % PALETTE.length]
    },
    laneStyle(id) {
      const g = this.laneGeo[id] || { x: 0, y: 0, w: 1400, h: 160 }
      return { left: g.x + 'px', top: g.y + 'px', width: g.w + 'px', height: g.h + 'px' }
    },
    stepStyle(gongyiId, gongxuId) {
      const g = this.stepGeo[gongyiId + '-' + gongxuId] || { x: 12, y: 12, w: 150, h: 76 }
      return { left: g.x + 'px', top: g.y + 'px', width: g.w + 'px', height: g.h + 'px' }
    },
    setZoom(z) {
      this.zoom = Math.max(0.3, Math.min(2, Math.round(z * 100) / 100))
    },
    zoomIn() {
      this.setZoom(this.zoom + 0.1)
    },
    zoomOut() {
      this.setZoom(this.zoom - 0.1)
    },
    zoomReset() {
      this.zoom = 1
    },
    saveView() {
      try {
        localStorage.setItem('prod_process_view', JSON.stringify({ zoom: this.zoom, showArrows: this.showArrows }))
      } catch (e) {
        /* ignore */
      }
    },
    loadView() {
      try {
        const s = localStorage.getItem('prod_process_view')
        if (s) {
          const o = JSON.parse(s) || {}
          if (o.zoom) this.zoom = o.zoom
          if (typeof o.showArrows === 'boolean') this.showArrows = o.showArrows
        }
      } catch (e) {
        /* ignore */
      }
    }
  }
}
</script>

<style lang="less" scoped>
.page-main {
  .page-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
    .title {
      font-size: 15px;
      font-weight: bold;
    }
  }
  .content {
    display: flex;
    .canvas-wrap {
      flex: 1;
      min-width: 0;
      overflow: auto;
      &::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }
      &::-webkit-scrollbar-thumb {
        background: #c0c4cc;
        border-radius: 4px;
      }
      &::-webkit-scrollbar-thumb:hover {
        background: #909399;
      }
      &::-webkit-scrollbar-track {
        background: transparent;
      }
      border: 1px solid #e4e7ed;
      border-radius: 4px;
      background-color: #f7f8fa;
      background-image: linear-gradient(#e8eaef 1px, transparent 1px), linear-gradient(90deg, #e8eaef 1px, transparent 1px);
      background-size: 20px 20px;
      .canvas-sizer {
        position: relative;
      }
      .canvas {
        position: absolute;
        top: 0;
        left: 0;
        .canvas-empty {
          position: absolute;
          top: 40px;
          left: 40px;
          color: #909399;
        }
        .links {
          position: absolute;
          top: 0;
          left: 0;
          pointer-events: none;
          z-index: 1;
        }
      }
    }
    .detail-panel {
      width: 260px;
      margin-left: 10px;
      padding: 10px;
      border: 1px solid #e4e7ed;
      border-radius: 4px;
      background: #fff;
      overflow: auto;
      &::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }
      &::-webkit-scrollbar-thumb {
        background: #c0c4cc;
        border-radius: 4px;
      }
      &::-webkit-scrollbar-track {
        background: transparent;
      }
      .detail-title {
        font-size: 15px;
        font-weight: bold;
        margin-bottom: 6px;
      }
      .detail-sub {
        font-size: 12px;
        color: #606266;
        line-height: 20px;
      }
      .detail-divider {
        border-top: 1px dashed #dcdfe6;
        margin: 8px 0;
      }
      .detail-count {
        font-size: 12px;
        margin-bottom: 6px;
      }
      .detail-list {
        max-height: 320px;
        overflow: auto;
        .detail-row {
          padding: 6px;
          border-bottom: 1px solid #f0f0f0;
          .dr-name {
            font-size: 13px;
          }
          .dr-info {
            display: flex;
            justify-content: space-between;
            font-size: 12px;
            .dr-qty {
              color: #e6a23c;
            }
          }
        }
      }
    }
  }
}

.lane-block {
  position: absolute;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #c8ccd4;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  z-index: 2;
  &.active {
    border-color: #396bf6;
    box-shadow: 0 0 0 2px rgba(57, 107, 246, 0.25);
  }
  .lane-head {
    height: 34px;
    line-height: 34px;
    padding: 0 10px;
    color: #fff;
    border-radius: 6px 6px 0 0;
    cursor: move;
    white-space: nowrap;
    overflow: hidden;
    .lane-name {
      font-size: 13px;
      font-weight: bold;
    }
    .lane-meta {
      float: right;
      font-size: 11px;
      color: rgba(255, 255, 255, 0.85);
    }
  }
  .lane-body {
    position: absolute;
    top: 34px;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: hidden;
  }
}

.step-box {
  position: absolute;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-top: 3px solid #409eff;
  border-radius: 4px;
  padding: 4px 6px;
  cursor: move;
  overflow: hidden;
  font-size: 12px;
  z-index: 3;
  &.active {
    box-shadow: 0 0 0 2px rgba(57, 107, 246, 0.35);
    border-color: #396bf6;
  }
  .step-idx {
    position: absolute;
    top: 2px;
    right: 4px;
    font-size: 10px;
    color: #c0c4cc;
    font-weight: bold;
  }
  .step-name {
    font-weight: bold;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .step-info {
    color: #909399;
    font-size: 11px;
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.resize-handle {
  position: absolute;
  width: 12px;
  height: 12px;
  right: 0;
  bottom: 0;
  cursor: nwse-resize;
  background: linear-gradient(135deg, transparent 40%, #396bf6 40%, #396bf6 55%, transparent 55%, transparent 70%, #396bf6 70%, #396bf6 85%, transparent 85%);
}
.lane-resize {
  z-index: 4;
}
.step-resize {
  z-index: 4;
  width: 9px;
  height: 9px;
}
</style>
